import {Component, HostListener, OnInit, ViewChild, signal, computed, linkedSignal, OnDestroy} from '@angular/core'
import {IonicModule, IonPopover} from '@ionic/angular'
import {TranslateModule, TranslateService} from '@ngx-translate/core'
import {TranslateConfigService} from '../../services/translate-config.service'
import {ConfigService} from 'src/app/services/config/config.service'
import {UserService} from 'src/app/services/user/user.service'
import {UtilsService} from 'src/app/services/utils/utils.service'
import {MenuController} from '@ionic/angular'
import {ProfileService} from "../../services/api/profile.service";
import {AuthService} from "../../services/auth.service";
import {Router, RouterModule, NavigationEnd} from "@angular/router";
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import { NotificationService } from 'src/app/services/api/notification.service';
import { AppStorageService } from '../../services/app-storage.service';
import { StorageKeys } from '../../services/storage-keys';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { WebNotificationsComponent } from '../web-notifications/web-notifications.component';
import { DialogComponent } from '../dialog/dialog.component';

@Component({
  selector: 'app-header-web',
  templateUrl: './header-web.component.html',
  styleUrls: ['./header-web.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, FormsModule, RouterModule, TranslateModule, WebNotificationsComponent, DialogComponent]
})
export class HeaderWebComponent implements OnInit, OnDestroy {
  @ViewChild(IonPopover) popover!: IonPopover

  private routerSubscription!: Subscription;

  constructor(
  private translate: TranslateService,
    private configService: ConfigService,
  private translateConfig: TranslateConfigService,
    private userService: UserService,
    private menu: MenuController,
    private profileService: ProfileService,
    private authService: AuthService, // Asumiendo que UserService maneja autenticación
    protected router: Router,
    private notificationService: NotificationService,
    private storage: AppStorageService
  ) {
  }

  // Estado convertido a signals
  isLoggedIn = signal<boolean>(false);
  show = signal<boolean>(false);
  showLogout = signal<boolean>(false);
  notificationSelected = signal<any>({});
  selectedLanguage = signal<any>('es');
  langs = signal<any>(null);
  // Toggle for mobile side-menu language dropdown
  showLangs = signal<boolean>(false);
  user = signal<any>(null);
  userMenu = signal<any[]>([]);
  isMobile = signal<boolean>(false);
  hasUnreadNotifications = signal<boolean>(false);
  private notificationCountSub?: Subscription;
  /** Timestamp to avoid immediate re-open of logout dialog (bounce clicks) */
  private lastLogoutCloseAt = 0;
  mainMenu = signal<any[]>([
    {
      label: this.translate.instant('HEADER_WEB.LOGIN'),
      icon: '../../assets/svg/account.svg',
      link: '/login'
    },
    {
      label: this.translate.instant('HEADER_WEB.REGISTER'),
      icon: '../../assets/svg/caret-forward-outline.svg',
      link: '/register'
    }
  ]);
  menuToShow = linkedSignal<any[]>(() => this.isLoggedIn() ? this.userMenu() : this.mainMenu());


  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.checkScreenSize()
  }

  getUnreadNotificationCount() {
    const count = this.storage.get(StorageKeys.NOTIFICATION_COUNT);
    if (count) {
      this.hasUnreadNotifications.set(parseInt(count, 10) > 0);
    } else {
      this.hasUnreadNotifications.set(false);
    }
  }

  checkScreenSize() {
    this.isMobile.set(window.innerWidth < 600);
  }

  async ngOnInit() {
    this.getUnreadNotificationCount();
    this.checkScreenSize()
    const user = await this.userService.getUser()
    this.profileService.getPublicProfile().subscribe({
        next: (profile) => {
          if (user && profile) {
            user.publicProfile = profile
            this.user.set(user)
          }
        },
        error: (error) => {
          },
        complete: () => {
          this.initializeMenus()
        }
      }
    )

  this.langs.set(await this.configService.getLangs())
  this.selectedLanguage.set(await this.configService.getSelectedLang())
    // Mantener isLoggedIn en sincronía con AuthService
    this.isLoggedIn.set(this.authService.isAuthenticated())
    this.authService.isAuthenticated$().subscribe((val) => {
      this.isLoggedIn.set(!!val);
    });

    // Suscribirse a cambios de idioma para actualizar menús
    this.translate.onLangChange.subscribe(() => {
      this.initializeMenus()
    });

    // Listener para cambios de navegación
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        // Cerrar el menú lateral cuando cambia la URL
        if (this.isMobile()) {
          this.closeMenu();
        }
        this.getUnreadNotificationCount(); // Actualizar contador de notificaciones
      });

    // Subscribe to the reactive notification count so desktop header updates live
    this.notificationCountSub = this.notificationService.notificationCount$.subscribe((count) => {
      try {
        this.hasUnreadNotifications.set((count || 0) > 0);
      } catch (e) {
      }
    });
  }

  private initializeMenus() {
    this.mainMenu.set([
      {
        label: this.translate.instant('HEADER_WEB.LOGIN'),
        icon: '../../assets/svg/account.svg',
        link: '/login'
      },
      {
        label: this.translate.instant('HEADER_WEB.REGISTER'),
        icon: '../../assets/svg/caret-forward-outline.svg',
        link: '/register'
      }
    ])

    this.userMenu.set([
      {
        label: this.translate.instant('HEADER_WEB.MESSAGES'),
        link: '/app/chat',
        icon: '../../assets/svg/messages.svg'
      },
      {
        label: this.translate.instant("HEADER_WEB.ACCOUNT"),
        icon: '../../assets/svg/account.svg',
        link: '/app/account',
        avatar: this.user()?.publicProfile?.photo_perfil
      }
    ])
  }

  closeBox() {
    try {
      // Reset both modal signals to avoid re-opening loops via (toggle) bindings
      this.show.set(false);
      this.showLogout.set(false);
      if (this.popover) {
        this.popover.dismiss().catch(()=>{});
      }
    } catch (e) {
      // swallow
    }
  }

  selectLanguage(language
                 :
                 any
  ) {
  this.selectedLanguage.set(language)
  this.translateConfig.setLanguage(language.value)
  }

  // Used in the mobile side menu: change language and reload the app
  selectLanguageAndReload(language: any) {
  this.selectedLanguage.set(language);
  this.translateConfig.setLanguage(language.value);
    // Close the menu before reloading to avoid UI glitches
    try { this.closeMenu(); } catch {}
    // Force a full reload so static assets/labels update everywhere
    setTimeout(() => window.location.reload(), 100);
  }

  toggleLangs() {
    this.showLangs.set(!this.showLangs());
  }

  toggleShow(notification
             :
             any
  ) {
    this.notificationSelected.set(notification.date ? notification : this.notificationSelected())
  // Toggle locally; don't call closeBox() (which also manipulates showLogout) to prevent race causing reopen
  this.show.set(!this.show())
  // If popover open, dismiss it safely
  try { this.popover?.dismiss(); } catch {}
  }

  toggleLogout() {
  // Fully deprecated: keep no-op body to avoid runtime errors if referenced somewhere compiled
  // Force close instead of toggling to avoid reopen loops.
  this.showLogout.set(false);
  }

  openLogoutDialog() {
  // Prevent accidental re-open within 300ms of a close (double event / bubbling)
  const now = Date.now();
  if (now - this.lastLogoutCloseAt < 300) return;
  this.showLogout.set(true);
  try { this.popover?.dismiss(); } catch {}
  }

  closeLogoutDialog() {
  this.showLogout.set(false);
  this.lastLogoutCloseAt = Date.now();
  }

  confirm() {
    this.authService.logout().subscribe({
      next: () => {
        // Asegurar limpieza local y estado
        try {
          this.storage.remove(StorageKeys.TOKEN)
          this.storage.remove(StorageKeys.USER)
          this.storage.set(StorageKeys.GUEST, 'true')
        } catch (e) {
          }
        this.isLoggedIn.set(false);
        // Navegar a /login en web siempre que sea posible
        try {
          this.router.navigate(['/login']);
        } catch (e) {
          }
      },
      error: (error) => {
        // Aun si falla la petición, forzamos limpieza y navegación para reflejar el estado local
        try {
          this.storage.remove(StorageKeys.TOKEN)
          this.storage.remove(StorageKeys.USER)
          this.storage.set(StorageKeys.GUEST, 'true')
        } catch (e) {
          }
        this.isLoggedIn.set(false);
        try {
          this.router.navigate(['/login']);
        } catch (e) {
          }
      }
    });
  }

  openMenu() {
    this.menu.open('mobileMenu')
  }

  closeMenu() {
    this.menu.close('mobileMenu')
  }

  ngOnDestroy() {
    // Cancelar la suscripción al cambiar de componente
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.notificationCountSub) {
      this.notificationCountSub.unsubscribe();
    }
  }
}
